import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";

function ContactInfo({ user }) {
  const { t } = useTranslation();
  const [userSocial, setUserSocial] = useState({});

  const socialMediaSVGPathList = {
    Facebook: {
      bgColor: "bg-slate-800",
      link: "M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z",
    },
    Instagram: {
      bgColor: "bg-slate-800",
      link: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z",
    },
    LinkedIn: {
      bgColor: "bg-slate-800",
      link: "M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z",
    },
    Youtube: {
      bgColor: "bg-slate-800",
      link: "M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z",
    },
  };

  useEffect(() => {
    if (user?.metadata?.socialMedia) {
      try {
        const parsedSocialMedia = user.metadata.socialMedia;
        const tempUserSocial = {};
        parsedSocialMedia.forEach((item) => {
          Object.assign(tempUserSocial, item);
        });
        setUserSocial(tempUserSocial);
      } catch (error) {
        console.error("Invalid socialMedia format", error);
        setUserSocial({});
      }
    }
  }, [user]);

  return (
    <div className="w-full max-w-3xl mx-auto bg-white p-6">
      <h1 className="text-2xl font-semibold text-slate-800 text-center mb-6">{t("contactInfo")}</h1>

      {/* Contact Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 items-center text-slate-800 gap-3">
        {user?.name && (
          <div className="flex items-center justify-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.7 0 5-2.3 5-5s-2.3-5-5-5-5 2.3-5 5 2.3 5 5 5zm0 2c-3.3 0-10 1.7-10 5v3h20v-3c0-3.3-6.7-5-10-5z" />
            </svg>
            <p className="text-base text-gray-500 font-semibold">{user.name}</p>
          </div>
        )}

        {user?.phoneNumber && (
          <div className="flex items-center justify-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6.6 10.8c1.2 2.4 3.3 4.5 5.7 5.7l1.9-1.9c.3-.3.8-.4 1.2-.3 1.3.4 2.7.6 4.1.6.7 0 1.2.6 1.2 1.2V20c0 .7-.6 1.2-1.2 1.2C10.3 21.2 2.8 13.7 2.8 4.8 2.8 4.1 3.4 3.6 4 3.6h3.2c.7 0 1.2.6 1.2 1.2 0 1.4.2 2.8.6 4.1.1.4 0 .8-.3 1.1l-1.9 1.8z" />
            </svg>
            <p className="text-base text-gray-500 font-semibold">{user.phoneNumber}</p>
          </div>
        )}

        {user?.email && (
          <div className="flex items-center justify-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
            </svg>
            <p className="text-base text-gray-500 font-semibold">{user.email}</p>
          </div>
        )}

        {user?.metadata?.website && (
          <div className="flex items-center justify-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#000000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <a
              href={
                user.metadata.website?.startsWith('http://') || user.metadata.website?.startsWith('https://')
                  ? user.metadata.website
                  : `https://${user.metadata.website}`
              } target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 font-semibold underline break-all"
            >
              {user.metadata.website}
            </a>
          </div>
        )}
      </div>

      {/* Social Media Links (Only shown if data exists) */}
      {Object.keys(userSocial).length > 0 && (
        <div className="flex flex-wrap justify-center gap-4 mt-6">
          {Object.entries(userSocial).map(([key, value]) => (
            <button
              key={key}
              className={`rounded-full p-3 text-white shadow-md transition hover:shadow-lg ${socialMediaSVGPathList[key]?.bgColor || "bg-slate-500"
                }`}
              onClick={() => {
                const validUrl = value.startsWith("http://") || value.startsWith("https://")
                  ? value
                  : `https://${value}`;
                window.open(validUrl, "_blank");
              }}            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d={socialMediaSVGPathList[key]?.link || ""} />
              </svg>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

ContactInfo.propTypes = {
  user: PropTypes.object.isRequired,
};

export default ContactInfo;